/**
 * Spanish copy for the `/pros` landing page.
 *
 * Shipped in the page bundle rather than fetched: the toggle in the sticky bar
 * swaps this object in for `PROS_STRINGS`, so switching language is a re-render
 * with nothing to load.
 *
 * Asset URLs are read back off the English strings instead of being repeated —
 * the photos, videos and payout figures are the same in both locales, and a
 * second copy of every S3 path is a set of URLs to keep in sync for no reason.
 */

import type { ProsStrings } from 'src/features/pros/pros.strings';
import { PROS_APPLY_MODAL, PROS_STRINGS } from 'src/features/pros/pros.strings';

const EN = PROS_STRINGS;

/** Spanish copy for the Apply modal. Only what is Pro-specific lives here —
 *  the form's own labels are the shared modal's copy, translated alongside it
 *  in `src/modals/ask-a-question/strings.es.ts`.
 *
 *  `value` and `shortLabel` deliberately stay as they are: the value is what
 *  the API route routes its Slack mention on, and `shortLabel` is what Slack
 *  shows the ops team, who read the channel in English. Only `label` — the
 *  line the applicant reads in the dropdown — is translated. */
export const PROS_APPLY_MODAL_ES = {
  title: 'Únete a la red de Pros de Belong',
  subtitle:
    'Cuéntanos a qué te dedicas y nos pondremos en contacto contigo para explicarte los siguientes pasos para unirte a la red.',
  positions: [
    {
      label: 'Pro (oficios y mantenimiento)',
      shortLabel: PROS_APPLY_MODAL.positions[0].shortLabel,
      value: PROS_APPLY_MODAL.positions[0].value,
    },
    {
      label: 'Anfitrión de visitas',
      shortLabel: PROS_APPLY_MODAL.positions[1].shortLabel,
      value: PROS_APPLY_MODAL.positions[1].value,
    },
    {
      label: 'Inspector',
      shortLabel: PROS_APPLY_MODAL.positions[2].shortLabel,
      value: PROS_APPLY_MODAL.positions[2].value,
    },
  ],
} as const;

export const PROS_STRINGS_ES: ProsStrings = {
  seo: {
    title:
      'Red de Pros de Belong | Trabajo constante para profesionales del hogar',
    description:
      'Como Pro de Belong, tendrás acceso a decenas de trabajos al mes en una red de casas en renta donde a la gente le encanta vivir. Horarios flexibles, pagos rápidos y sin tener que reclamar pagos pendientes.',
    imageAlt: 'Un Pro de Belong trabajando en una casa',
  },
  hero: {
    logoAlt: 'Red de Pros de Belong',
    headingLine1: 'BIENVENIDO A LA',
    headingLine2: 'RED DE PROS DE BELONG.',
    description:
      'Como Pro de Belong, tendrás acceso a decenas de trabajos al mes en una red de casas en renta donde a la gente le encanta vivir.',
    cta: 'Postúlate',
  },
  benefits: {
    label: 'POR QUÉ LOS PROS ELIGEN BELONG',
    heading: 'Trabajo para hacer crecer tu negocio.',
    items: [
      {
        title: 'Horarios\nflexibles',
        description:
          'Elige los trabajos que se ajusten a tu semana. Tú decides cuáles aceptas y cuándo los realizas.',
      },
      {
        title: 'Ingresos\nestables',
        description:
          'Decenas de trabajos al mes en una red de casas en crecimiento para mantener tu agenda llena.',
      },
      {
        title: 'Pagos\nrápidos',
        description:
          'Reporta el trabajo completado y cobra pronto. Sin tener que reclamar pagos pendientes ni esperar a que los propietarios paguen.',
      },
      {
        title: 'Reconocimiento y\nmejores tarifas',
        description:
          'Los Pros con mejor desempeño reciben más trabajos, tarifas más altas y prioridad en la programación de servicios. Tu trabajo se reconoce y se recompensa.',
      },
    ],
  },
  video: {
    heading: '¿Qué significa ser un Pro de Belong?',
  },
  standards: {
    quoteLines: [
      'No solo estás prestando un servicio.',
      'Estás mejorando la experiencia de alguien en su hogar.',
    ],
    author: 'Tamara Epelbaum',
    authorRole:
      'Directora de operaciones estratégicas globales y prestación de servicios',
    itemsLabel: 'NUESTROS ESTÁNDARES',
    imageAlt: 'Pros de Belong trabajando en casas de toda la red',
    // Noun phrases rather than the English imperatives: the roller is a
    // fixed-height, `nowrap` line, and "Haz un trabajo excelente" runs past
    // the half-width column it sits in from `lg` up.
    items: [
      'Puntualidad',
      'Respeto',
      'Comunicación clara',
      'Trabajo de calidad',
    ],
  },
  localNetwork: {
    label: 'NUESTRA RED LOCAL',
    heading: 'Personas con las que puedes contar.',
    description:
      'Tres roles se encargan de mantener, mostrar y cuidar cada casa de Belong, con atención presencial en todas las zonas donde operamos.',
    roles: [
      {
        title: 'Pros',
        imageSrc: EN.localNetwork.roles[0].imageSrc,
        imageAlt: 'Un Pro de Belong trabajando dentro de una casa',
        summary: 'Mantienen la casa\nen excelente estado.',
        description:
          'Profesionales de distintos oficios, con licencia y evaluados por Belong, que realizan el trabajo directamente, sin cadenas de subcontratación. Cada Pro recibe una calificación después de cada visita, por lo que los mejor valorados reciben más trabajo.',
        stats: [
          { label: 'Red', value: 'Más de 2,000 Pros' },
          { label: 'Total pagado', value: 'Más de $6.7M' },
          { label: 'Calificación', value: '4.8 de 5' },
        ],
      },
      {
        title: 'Anfitriones de visitas',
        imageSrc: EN.localNetwork.roles[1].imageSrc,
        imageAlt:
          'Un anfitrión de visitas de Belong mostrando una casa a posibles residentes',
        summary: 'Muestran la casa\na los futuros residentes.',
        description:
          'Reciben en la casa a quienes están interesados en vivir allí. Abren la puerta, responden preguntas durante el recorrido y se aseguran de dejar la casa bien cerrada al terminar la visita.',
        stats: [
          { label: 'Red', value: 'Más de 300 anfitriones' },
          { label: 'Mejores anfitriones', value: '$3,500+/mes' },
          { label: 'Calificación', value: '4.9 de 5' },
        ],
      },
      {
        title: 'Inspectores',
        imageSrc: EN.localNetwork.roles[2].imageSrc,
        imageAlt:
          'Un inspector de Belong revisando que una habitación cumpla con los estándares de Belong',
        summary: 'Verifican el cumplimiento\nde los estándares de Belong.',
        description:
          'Revisan en persona el estado de la casa al incorporarla a Belong y cuando los residentes se mudan a ella o la dejan. Su evaluación sirve de referencia para las reparaciones, los depósitos de garantía y los reclamos por daños.',
        stats: [
          { label: 'Red', value: 'Más de 250 inspectores' },
          { label: 'Mejores inspectores', value: '$4,000+/mes' },
          { label: 'Calificación', value: '4.8 de 5' },
        ],
      },
    ],
    applyCta: 'Postúlate',
    footnote:
      'Tres roles, un mismo estándar. Cada Pro, anfitrión de visitas e inspector representa a Belong en el hogar de alguien. Todos pasan por un proceso de evaluación y capacitación, y deben cumplir los mismos estándares de servicio de Belong (Open Standards), para ofrecer una experiencia consistente sin importar quién llegue a la puerta.',
  },
  payment: {
    label: 'CUÁNDO RECIBES TU PAGO',
    heading: 'Tu pago se libera al completar el trabajo.',
    description:
      'Completa el trabajo y confirma su finalización en el mismo hilo de correo para que se libere el pago. Sin tener que reclamar pagos ni enviar recordatorios.',
    steps: [
      'Trabajo completado',
      'Cierre confirmado por correo',
      'Pago liberado',
    ],
    scopeNote: '*Por ahora solo para Pros',
    loopHeading: 'Y así crecen tus oportunidades.',
    loopSteps: [
      'Recibes\nun correo',
      'Responde\npara aceptar',
      'La visita\nqueda confirmada',
      'Haz el\ntrabajo',
      'Comprobante\ncompletado',
      'Recibe\ntu pago',
    ],
    paidBadge: 'AQUÍ COBRAS',
    paidBadgeStepIndex: 5,
  },
  work: {
    heading: 'Los trabajos que te ofrecemos',
    paidLabel: 'pagados a Pros',
    imageAltPattern: 'Un Pro de Belong trabajando: {trade}',
    trades: [
      { label: 'Pintura', paid: '$2.3M', imageSrc: EN.work.trades[0].imageSrc },
      {
        label: 'Plomería',
        paid: '$1.9M',
        imageSrc: EN.work.trades[1].imageSrc,
      },
      {
        label: 'Limpieza',
        paid: '$1.8M',
        imageSrc: EN.work.trades[2].imageSrc,
      },
      {
        label: 'Climatización',
        paid: '$1.1M',
        imageSrc: EN.work.trades[3].imageSrc,
      },
      {
        label: 'Electricidad',
        paid: '$400K',
        imageSrc: EN.work.trades[4].imageSrc,
      },
      {
        label: 'Jardinería',
        paid: '$300K',
        imageSrc: EN.work.trades[5].imageSrc,
      },
      {
        label: 'Cerrajería',
        paid: '$100K',
        imageSrc: EN.work.trades[6].imageSrc,
      },
      {
        label: 'Contratista general',
        paid: '$2.5M',
        imageSrc: EN.work.trades[7].imageSrc,
      },
      {
        label: 'Otros servicios',
        paid: '$6.7M',
        imageSrc: EN.work.trades[8].imageSrc,
      },
    ],
  },
  social: {
    heading: 'Más de 2,000 Pros y seguimos creciendo',
    ratingScore: '4.5 de 5',
    ratingOn: 'en',
    ratingSource: 'Trustpilot',
    testimonials: [
      {
        imageSrc: EN.social.testimonials[0].imageSrc,
        quote:
          '"Trabajar con Belong ha transformado mi negocio. Los pagos siempre llegan a tiempo y los propietarios valoran de verdad el trabajo bien hecho."',
        author: 'Mike, especialista en climatización',
        tenure: '3 años con Belong',
      },
      {
        imageSrc: EN.social.testimonials[1].imageSrc,
        quote:
          '"Me encanta trabajar con Carla y el equipo. Son los mejores y siempre están disponibles para responder mis preguntas. ¡Pueden contar conmigo para lo que necesiten!"',
        author: 'John, especialista en climatización',
        tenure: '4 años con Belong',
      },
    ],
  },
  faq: {
    heading: 'Preguntas frecuentes',
    items: [
      {
        id: 'become-a-pro',
        question: '¿Cómo me convierto en un Pro de Belong?',
        answer:
          'Empieza por solicitar unirte a la red. Te preguntaremos sobre tu negocio, los oficios que desempeñas y las zonas donde prestas servicio. Después, te guiaremos por un breve proceso de incorporación. Una vez que tengamos registrados tus datos, tu información fiscal y la documentación de tu seguro, podrás aceptar trabajos.',
      },
      {
        id: 'onboarding',
        question: '¿En qué consiste el proceso de incorporación?',
        answer:
          'Son siete pasos breves: bienvenida, capacitación guiada, datos personales, información fiscal, seguro, configuración de la cuenta para recibir pagos y, por último, firma del acuerdo. La mayoría de los Pros completa el proceso en una sola sesión.',
      },
      {
        id: 'getting-paid',
        question: '¿Cómo y cuándo me pagan?',
        answer:
          'Reporta el trabajo completado a través de Belong y el pago se envía a la cuenta que vinculaste durante el proceso de incorporación. No tienes que facturar directamente a los propietarios ni esperar a que te paguen. Belong se encarga.',
      },
      {
        id: 'licensing-and-insurance',
        question: '¿Qué licencias y seguros necesito?',
        answer:
          'Los requisitos dependen de tu oficio y de los estados donde trabajas. En la etapa de verificación del seguro te indicaremos exactamente qué documentos de licencia y cobertura debes cargar según los trabajos que quieras aceptar.',
      },
      {
        id: 'work-volume',
        question: '¿Cuántos trabajos puedo recibir?',
        answer:
          'Los Pros tienen acceso a decenas de trabajos al mes en las zonas donde operamos. La cantidad depende de tus oficios, tu zona de servicio y cuántos trabajos decidas aceptar. Tú siempre decides si aceptas cada trabajo.',
      },
      {
        id: 'get-more-work',
        question: '¿Cómo consigo más trabajo?',
        answer:
          '**Mientras más rápido respondas, más trabajo recibes.** Las órdenes de trabajo llegan por correo electrónico, según tu oficio y tu zona, y la mayoría se asigna al primer Pro que responde en el hilo. Sin apps que revisar y sin licitaciones.\n\nA partir de ahí, todo depende de cómo manejes cada trabajo. Confirma el horario y llega dentro de él, y si algo cambia, avísanos con al menos tres horas de anticipación. Al llegar, preséntate como parte de la red de Belong. **Envía tu presupuesto para aprobación antes de empezar, ya que las reparaciones hechas sin aprobación previa no se reembolsan. Mantén todas las conversaciones sobre precios y pagos con Belong, nunca con los residentes.**\n\nCuando termine tu horario, te enviaremos un correo para confirmar si el trabajo está terminado. **Responde en menos de una hora con fotos del trabajo completado.** Una vez que el residente confirme la reparación, tu pago se libera en un plazo de 2 días.\n\n**Cada visita se califica.** Los Pros que responden rápido, se comunican con claridad y hacen el trabajo bien a la primera reciben más trabajos, mejores tarifas y prioridad en la programación.',
      },
      {
        id: 'kinds-of-work',
        question: '¿Qué tipo de trabajos ofrece Belong?',
        answer:
          'Mantenimiento y reparaciones en una red de casas en renta: preparación de la casa entre la salida de un residente y la llegada del siguiente, mantenimiento programado y reparaciones a pedido. Belong se encarga de la relación con el propietario y el residente para que tú puedas concentrarte en el trabajo.',
      },
    ],
  },
  closing: {
    heading: 'Aquí tienes tu lugar',
    description:
      'Miles de Pros ya hacen crecer su negocio con Belong. Si ofreces un trabajo de calidad y buscas oportunidades constantes, nos gustaría conocerte.',
    cta: 'Postúlate',
  },
};
